import { NOCFO_PLAN_OPTIONS, getSettingsPageUrl } from '@constants'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { useAllowUpload } from '@root/hooks/useAllowUpload'
import { useDocumentLimitStatus, useFillRuleFromSuggestion } from '@root/hooks'
import { setFiles, uploadFile, useTrialLimits } from '@root/query'
import { IAccountingRule } from '@root/query/accountingRules'
import { AccountingSuggestionKey, IAccountingSuggestion } from '@root/query/suggestions'
import { getOptionLabelByValue, setAPIErrors } from '@utils'
import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { SuggestionCardContainer } from './SuggestionCard/styles'
import {
  SuggestionCardAlerts,
  SuggestionCardFooter,
  SuggestionCardFrontContent,
  SuggestionCardModals
} from './SuggestionCard/components'
import { SuggestionCardFormValues, SuggestionCardProps } from './SuggestionCard/types'
import { areAttachmentIdsEqual, getDefaultPostingMode } from './SuggestionCard/utils'
import { SuggestionUncertaintyProvider } from './SuggestionCard/uncertainty/SuggestionUncertaintyContext'
import { resolveAcceptSuggestionError } from './resolveAcceptSuggestionError'

export const SuggestionCard: React.FC<SuggestionCardProps> = ({
  suggestion,
  onAccept,
  document
}) => {
  const timelineId = useId()
  const formId = `${timelineId}-suggestion-form`
  const { t } = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const queryClient = useQueryClient()

  const [showEdit, setShowEdit] = useState(false)
  const [editSuggestionSnapshot, setEditSuggestionSnapshot] =
    useState<IAccountingSuggestion | null>(null)
  const [showAccountingRuleForm, setShowAccountingRuleForm] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [defaultRule, setDefaultRule] = useState<Partial<IAccountingRule> | null>(null)
  const attachmentMutationQueueRef = useRef(Promise.resolve())

  const hasBookkeepingFilesPermission = usePermission(PermissionSet.BOOKKEEPING_FILES)
  const isAllowedToUpload = useAllowUpload()
  const { isDocumentLimitReached, documentLimits } = useDocumentLimitStatus(suggestion?.date)
  const isTrialing = business?.stripe_plan_status === 'trialing'
  const { data: trialLimits } = useTrialLimits({ enabled: isTrialing })
  const trialLimitReached =
    isTrialing &&
    trialLimits?.document_usage?.limit != null &&
    (trialLimits?.document_usage?.remaining ?? 0) <= 0
  const planName = getOptionLabelByValue(NOCFO_PLAN_OPTIONS, business?.subscription_plan, {
    t,
    fallback: '',
    fallbackToValue: true
  })
  const planSettingsUrl = getSettingsPageUrl(slug, { page: 'plan' })
  const { isLoading: isDefaultRuleLoading, getDefaultRule } = useFillRuleFromSuggestion(suggestion)

  const methods = useForm<SuggestionCardFormValues>({
    defaultValues: {
      description: suggestion.description,
      contact: suggestion.contact,
      attachment_ids: suggestion?.attachment_ids || [],
      tag_ids: suggestion?.tag_ids || [],
      relations: suggestion.suggested_relations ?? [],
      posting_mode: getDefaultPostingMode(suggestion),
      accrual_date: suggestion.accrual_date ?? suggestion.accrual_split?.suggested_date ?? null,
      blueprint: suggestion.blueprint
    }
  })
  const { control, handleSubmit, setValue, getValues, setError } = methods

  const attachmentIds = useWatch({
    control,
    name: 'attachment_ids'
  })
  const latestAttachmentIdsRef = useRef<number[]>(attachmentIds || [])

  useEffect(() => {
    latestAttachmentIdsRef.current = attachmentIds || []
  }, [attachmentIds])

  const isCardDropzoneEnabled = isAllowedToUpload && hasBookkeepingFilesPermission

  const setFilesMutation = useMutation({
    mutationFn: (ids: number[]) => setFiles({ slug, documentId: document.id }, ids),
    ...{
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [slug, 'documents'] })
        queryClient.invalidateQueries({ queryKey: [slug, 'suggestions', document.id] })
        toast.success(t('document.suggestionCard.filesUpdated'))
      },
      onError: () => {
        toast.error(t('document.suggestionCard.filesUpdatedError'))
      }
    }
  })

  const queueAttachmentUpdate = useCallback(
    async (getNextIds: (currentIds: number[]) => number[]) => {
      const runUpdate = async () => {
        const previousIds = latestAttachmentIdsRef.current
        const nextIds = Array.from(new Set(getNextIds(previousIds)))
        if (areAttachmentIdsEqual(previousIds, nextIds)) return

        latestAttachmentIdsRef.current = nextIds
        setValue('attachment_ids', nextIds, { shouldDirty: true })

        try {
          await setFilesMutation.mutateAsync(nextIds)
        } catch {
          latestAttachmentIdsRef.current = previousIds
          setValue('attachment_ids', previousIds)
          throw new Error('Failed to persist attachment ids')
        }
      }

      const queuedUpdate = attachmentMutationQueueRef.current.then(runUpdate)
      attachmentMutationQueueRef.current = queuedUpdate.catch(() => undefined)
      return queuedUpdate
    },
    [setFilesMutation, setValue]
  )

  const {
    getRootProps: getCardDropzoneRootProps,
    getInputProps: getCardDropzoneInputProps,
    isDragActive: isCardDragActive
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: true,
    disabled: !isCardDropzoneEnabled,
    noDragEventsBubbling: true,
    onDrop: async newFiles => {
      if (newFiles.length === 0) return
      let uploadedFiles: Awaited<ReturnType<typeof uploadFile>>[] = []
      try {
        uploadedFiles = await Promise.all(
          newFiles.map(file => uploadFile(file, { slug, fileName: file.name }))
        )
      } catch {
        toast.error(t('document.suggestionCard.filesUpdatedError'))
        return
      }
      await queueAttachmentUpdate(currentIds => [
        ...currentIds,
        ...uploadedFiles.map(file => file.id)
      ]).catch(() => undefined)
    }
  })

  const getSuggestionForEdit = useCallback((): IAccountingSuggestion => {
    const values = getValues()
    return {
      ...suggestion,
      description: values.description ?? suggestion.description,
      contact:
        typeof values.contact === 'number'
          ? values.contact
          : values.contact === null
          ? null
          : suggestion.contact,
      attachment_ids: values.attachment_ids ?? suggestion.attachment_ids,
      tag_ids: values.tag_ids ?? suggestion.tag_ids,
      suggested_relations: values.relations ?? suggestion.suggested_relations ?? [],
      posting_mode: values.posting_mode ?? suggestion.posting_mode,
      accrual_date: values.accrual_date ?? suggestion.accrual_date,
      blueprint: values.blueprint ?? suggestion.blueprint
    }
  }, [getValues, suggestion])

  const handleCreateRuleClick = async () => {
    const rule = await getDefaultRule(getValues('contact'))
    if (!rule) return
    setDefaultRule(rule)
    setShowAccountingRuleForm(true)
  }

  const handleAccept = handleSubmit(async () => {
    setLoading(true)
    try {
      const payload = getValues()
      await onAccept(suggestion.id, payload)
    } catch (error) {
      const { status, data: errorData } = error || {}
      if (status === 400) {
        if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
          setAPIErrors(errorData, setError)
        }
        const resolvedError = resolveAcceptSuggestionError(errorData)
        if (resolvedError) {
          toast.error(t(resolvedError))
          return
        }
      }
      toast.error(t('document.form.updateError'))
    } finally {
      setLoading(false)
    }
  })

  return (
    <>
      <FormProvider {...methods}>
        <SuggestionUncertaintyProvider
          suggestion={suggestion}
          attachmentExempt={document.attachment_exempt}
          onEdit={() => {
            setEditSuggestionSnapshot(getSuggestionForEdit())
            setShowEdit(true)
          }}
        >
          <SuggestionCardContainer>
            <SuggestionCardAlerts
              trialLimitReached={trialLimitReached}
              isDocumentLimitReached={isDocumentLimitReached}
              planName={planName}
              planSettingsUrl={planSettingsUrl}
              documentLimit={documentLimits?.limit ?? 0}
            />

            <SuggestionCardFrontContent
              suggestion={suggestion}
              formId={formId}
              handleAccept={handleAccept}
              showManualContactSelector={false}
              contactSelectorOpenRequestId={0}
              onShowManualContactSelector={() => undefined}
              onUseSuggestedContact={() => {
                setValue('contact', suggestion.contact ?? null, { shouldDirty: true })
              }}
              isCardDragActive={isCardDragActive}
              getCardDropzoneRootProps={getCardDropzoneRootProps}
              getCardDropzoneInputProps={getCardDropzoneInputProps}
              documentId={document.id}
              attachmentIds={attachmentIds ?? []}
              onAttachmentIdsChange={nextAttachmentIds => {
                void queueAttachmentUpdate(() => nextAttachmentIds)
              }}
            />

            <SuggestionCardFooter
              isLoading={isLoading}
              isDefaultRuleLoading={isDefaultRuleLoading}
              formId={formId}
              onEdit={() => {
                setEditSuggestionSnapshot(getSuggestionForEdit())
                setShowEdit(true)
              }}
              onAutomate={handleCreateRuleClick}
              isRuleWaitingForAttachment={
                suggestion.key === AccountingSuggestionKey.RuleWaitAttachment
              }
            />
          </SuggestionCardContainer>
        </SuggestionUncertaintyProvider>
      </FormProvider>

      <SuggestionCardModals
        suggestionForEdit={editSuggestionSnapshot ?? suggestion}
        attachmentExempt={document.attachment_exempt}
        showEdit={showEdit}
        showAccountingRuleForm={showAccountingRuleForm}
        defaultRule={defaultRule}
        onCloseEdit={() => {
          setShowEdit(false)
          setEditSuggestionSnapshot(null)
        }}
        onEditFormValuesChange={values => {
          if (values.relations !== undefined) {
            setValue('relations', values.relations, { shouldDirty: true })
          }
          if (values.blueprint !== undefined) {
            setValue('blueprint', values.blueprint, { shouldDirty: true })
          }
          if (values.posting_mode !== undefined) {
            setValue('posting_mode', values.posting_mode, { shouldDirty: true })
          }
          if (values.accrual_date !== undefined) {
            setValue('accrual_date', values.accrual_date, { shouldDirty: true })
          }
        }}
        onCreateSuggestion={data => onAccept(suggestion.id, data)}
        onCloseRuleForm={() => {
          setShowAccountingRuleForm(false)
          setDefaultRule(null)
        }}
        onRuleCreated={() => {
          setShowAccountingRuleForm(false)
          setDefaultRule(null)
          queryClient.invalidateQueries({ queryKey: [slug, 'suggestions', document.id] })
          queryClient.invalidateQueries({ queryKey: [slug, 'documents', document.id] })
        }}
      />
    </>
  )
}
